import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Link,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BentoCategory,
  Organization,
  OrganizationUser,
  StripeSubscription,
  SubscriptionType,
  UserIntegration,
  VAStatus,
} from "schemas/dashboard";
import { CustomEvent, Map, SetAlertType } from "schemas/functions";
import { OverridePlanName } from "schemas/payments";

import ConfirmDialog from "components/Dialogs/ConfirmDialog";
import { fetcherAuth } from "utils/api";
import { makeDeepCopy } from "utils/updateLocalState";

import AdminLoginAs from "../LoginAs";
import DetailedCategoryForm from "./DetailedCategoryForm";
import styles from "./styles";

interface Props {
  organizationId: number;
  organizationUser: OrganizationUser | null;
  setOrganizationUser: Dispatch<SetStateAction<OrganizationUser | null>>;
  setAlert: SetAlertType;
  setOrgUsers: Dispatch<SetStateAction<OrganizationUser[]>>;
}

export default function Settings({
  organizationId,
  setAlert,
  organizationUser,
  setOrganizationUser,
  setOrgUsers,
}: Props) {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const [organization, setOrganization] = useState<Organization | null>(null);
  const [needsUpdateCategory, setNeedsUpdateCategory] = useState(false);

  const [topLevelCategories, setTopLevelCategories] = useState<BentoCategory[]>(
    [],
  );
  const [selectedCategories, setSelectedCategories] = useState<BentoCategory[]>(
    [],
  );
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] =
    useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);

  const [googleIntegration, setGoogleIntegration] =
    useState<UserIntegration | null>(null);
  const [outreachPausedLoading, setOutreachPausedLoading] =
    useState<boolean>(false);
  const [agentEmailsText, setAgentEmailsText] = useState<string>("");
  const [agentEmailsLoading, setAgentEmailsLoading] = useState<boolean>(false);

  const handleConfirm = async () => {
    setDeleteLoading(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/admin/organizations/${organizationUser?.organizationId}`,
        "DELETE",
      );
      setAlert("Organization user deleted successfully", "success");
      setOrganizationUser(null);
      navigate(`/admin/organizations`);
      setOrgUsers((prevOrgUsers) =>
        prevOrgUsers.filter((orgUser) => orgUser.id !== organizationUser?.id),
      );
    } catch (error) {
      setAlert(
        error?.message || "An error occurred while deleting organization user",
        "error",
      );
    } finally {
      setDeleteLoading(false);
      setConfirmDeleteDialogOpen(false);
    }
  };

  const fetchCategories = async () => {
    setLoading(true);

    try {
      const res = await fetcherAuth(
        getToken,
        `/api/admin/bento-categories`,
        "GET",
      );
      setTopLevelCategories(res.topLevelCategories);
    } catch (error) {
      setAlert(error.message || "Unable to fetch categories", "error");
    } finally {
      setLoading(false);
    }
  };

  const [subscription, setSubscription] = useState<StripeSubscription | null>(
    null,
  );
  const fetchSubscription = async () => {
    try {
      const response = await fetcherAuth(
        getToken,
        `/api/admin/organization-users/${organizationUser?.id}/subscription`,
        "GET",
      );
      setSubscription(response.subscription);
    } catch (error) {
      setAlert("An error occurred while fetching subscription data", "error");
    }
  };

  const fetchGoogleIntegration = async (organizationUserId: number) => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/admin/organization-users/${organizationUserId}/google-integration`,
        "GET",
      );
      setGoogleIntegration(res.googleIntegration ?? null);
      setAgentEmailsText((res.googleIntegration?.agentEmails ?? []).join("\n"));
    } catch (error) {
      setAlert(
        error?.message || "An error occurred while fetching integration data",
        "error",
      );
    }
  };

  const fetchProfile = async (organizationId: number) => {
    setLoading(true);
    if (!organizationId) return;
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/admin/organization-profiles/${organizationId}`,
      );
      setOrganizationUser(res.organizationUser);
      setOrganization(res.organization);
      setSelectedCategories(res.organization.bentoCategories);
    } catch (error) {
      setAlert(
        error?.message ||
          "An error occurred, you do not have access to this page",
        "warning",
      );
    } finally {
      setLoading(false);
    }
  };

  const onChangeOrgUser = (name: string, value: any) => {
    setOrganizationUser((prevOrganizationUser) => {
      if (prevOrganizationUser) {
        return {
          ...prevOrganizationUser,
          [name]: value,
        };
      } else return prevOrganizationUser;
    });
  };

  const onChangeOrg = (name: string, value: any) => {
    setOrganization((prevOrganization: Organization | null) => {
      const copy = makeDeepCopy(prevOrganization);
      copy[name] = value;
      return copy;
    });
  };

  const handleUnpauseOutreach = async () => {
    if (!googleIntegration) return;
    setOutreachPausedLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/admin/user-integrations/${googleIntegration.id}/unpause-outreach`,
        "POST",
      );
      setGoogleIntegration(res.userIntegration);
      setAlert("Outreach unpaused", "success");
    } catch (error) {
      setAlert(
        error?.message || "An error occurred while unpausing outreach",
        "error",
      );
    } finally {
      setOutreachPausedLoading(false);
    }
  };

  const handleSaveAgentEmails = async () => {
    if (!googleIntegration) return;
    setAgentEmailsLoading(true);
    try {
      const emails = agentEmailsText
        .split("\n")
        .map((e) => e.trim())
        .filter(Boolean);
      const res = await fetcherAuth(
        getToken,
        `/api/admin/user-integrations/${googleIntegration.id}/agent-emails`,
        "PUT",
        {},
        { emails },
      );
      setGoogleIntegration(res.userIntegration);
      setAlert("Agent emails saved", "success");
    } catch (error) {
      setAlert(
        error?.message || "An error occurred while saving agent emails",
        "error",
      );
    } finally {
      setAgentEmailsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!organization) return;

    setSaveLoading(true);

    try {
      if (organizationUser) {
        await fetcherAuth(
          getToken,
          `/api/admin/organization-users/${organizationUser.id}`,
          "PUT",
          {},
          {
            doNotEmail: organizationUser.doNotEmail,
          },
        );
      }

      if (organization) {
        const body: Map = {};
        if (organization.vaStatus) {
          body.vaStatus = organization.vaStatus;
        }
        body.overridePlanName = organization?.overridePlanName;
        body.overrideEmailQuota = organization?.overrideEmailQuota;
        await fetcherAuth(
          getToken,
          `/api/admin/organizations/${organization.id}`,
          "PUT",
          {},
          body,
        );
      }
      if (organization && !organization?.vaStatus) {
        await fetcherAuth(
          getToken,
          `/api/admin/organizations/${organization.id}/remove-va-status`,
          "PUT",
        );
      }

      if (needsUpdateCategory) {
        await fetcherAuth(
          getToken,
          `/api/admin/organizations/${organization.id}/bento-categories`,
          "PUT",
          {},
          {
            categoryIds: selectedCategories?.map((x) => x.id),
          },
        );
      }
      setAlert("Profile saved", "success");
    } catch (error) {
      if (error?.message.includes("Google integration")) {
        setAlert(
          "Cannot approve VA user yet. User has not granted Gmail permissions to read / send email",
          "error",
        );
        setOrganization((prevOrganization: Organization | null) => {
          if (prevOrganization) {
            return {
              ...prevOrganization,
              vaStatus: VAStatus.pending,
            };
          } else return prevOrganization;
        });
        return;
      }
      setAlert(
        error.message || "An error occurred when updating profile",
        "warning",
      );
    } finally {
      setSaveLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile(organizationId);
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  useEffect(() => {
    if (organizationUser?.id) {
      fetchSubscription();
      fetchGoogleIntegration(organizationUser.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationUser?.id]);

  return (
    <Box sx={{ py: 2 }}>
      <Grid container sx={{ mb: 2 }} alignItems="center" gap={1}>
        Email: {organizationUser?.email} | Org ID:{" "}
        {organizationUser?.organizationId} | User ID: {organizationUser?.id}
        <AdminLoginAs
          organizationId={Number(organizationId)}
          setAlert={setAlert}
        />
      </Grid>

      <Divider sx={{ my: 2, width: 400 }} />

      <Typography variant="h6">Gmail Account</Typography>
      {googleIntegration ? (
        <>
          <Typography paragraph>
            <strong>Connected Email:</strong> {googleIntegration.email ?? "—"}
          </Typography>
          <Grid container alignItems="center" sx={{ mb: 2 }}>
            <strong>Outreach:</strong>&nbsp;
            {googleIntegration.outreachPaused ? "Paused" : "Active"}
            {googleIntegration.outreachPaused && (
              <LoadingButton
                loading={outreachPausedLoading}
                onClick={handleUnpauseOutreach}
                disableElevation
                variant="contained"
                size="small"
                sx={{ ml: 1 }}
              >
                Unpause
              </LoadingButton>
            )}
          </Grid>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Agent Contact Emails
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            One email per line. These addresses will not be created as outreach
            contacts during Gmail processing and emails from them will be
            treated like sent messages.
          </Typography>
          <TextField
            multiline
            minRows={3}
            maxRows={10}
            fullWidth
            size="small"
            value={agentEmailsText}
            onChange={(e) => setAgentEmailsText(e.target.value)}
            placeholder={"agent@example.com\nmanager@example.com"}
            sx={{ mb: 1, maxWidth: 400 }}
          />
          <Box>
            <LoadingButton
              loading={agentEmailsLoading}
              onClick={handleSaveAgentEmails}
              variant="outlined"
              size="small"
            >
              Save Agent Emails
            </LoadingButton>
          </Box>
        </>
      ) : (
        <Typography paragraph>No Gmail integration connected</Typography>
      )}

      <Divider sx={{ my: 2, width: 400 }} />

      <Box
        sx={{
          maxWidth: 600,
          border: "1px solid",
          borderColor: "error.main",
          p: 2,
          borderRadius: 4,
          my: 2,
        }}
      >
        <Typography variant="h6">Delete User Account</Typography>
        <Typography>
          <Box
            component="i"
            className="fa-regular fa-exclamation-triangle"
            sx={{ mr: 0.5 }}
          />{" "}
          Deleting this user will remove their access to Bento and all
          associated data. This action is irreversible.
        </Typography>
        <Button
          sx={{
            color: "error.main",
          }}
          disabled={
            subscription?.status === SubscriptionType.ACTIVE ||
            subscription?.status === SubscriptionType.PAST_DUE
          }
          onClick={() => setConfirmDeleteDialogOpen(true)}
        >
          Delete User
        </Button>
        {subscription?.status === SubscriptionType.ACTIVE && (
          <Typography>
            ⚠️ Cannot delete user with active subscription. Please cancel the
            subscription first.
          </Typography>
        )}
      </Box>

      <Divider />

      <Typography variant="h6">Subscription</Typography>

      <Grid item>
        <ul>
          <li>
            <strong>Plan Name</strong>: {subscription?.planName || "Free"}
          </li>
          <li>
            <strong>Stripe Customer Dashboard: </strong>{" "}
            {subscription?.stripeCustomerId ? (
              <Link
                target="_blank"
                rel="noreferrer"
                href={`https://dashboard.stripe.com/customers/${subscription?.stripeCustomerId}`}
              >
                https://dashboard.stripe.com/customers/
                {subscription?.stripeCustomerId}
              </Link>
            ) : (
              <Box>User has not subscribed to Bento</Box>
            )}
          </li>
        </ul>
      </Grid>

      <Divider sx={{ my: 2, width: 400 }} />

      <Typography variant="h6">Update Information</Typography>
      {organizationUser && (
        <Grid container gap={2}>
          <Grid item xs={12}>
            Do Not Email:{" "}
            <Switch
              checked={organizationUser.doNotEmail}
              onChange={(e: CustomEvent) =>
                onChangeOrgUser("doNotEmail", e.target.checked)
              }
              disabled={loading}
              name="doNotEmail"
            />
            {organizationUser.doNotEmail
              ? "User will not receive emails"
              : "User will receive emails"}
          </Grid>

          <Typography variant="h6">Override Settings</Typography>
          <Grid item xs={12} container alignItems="center" gap={1}>
            <Box sx={styles.label}>Override Email Quota (Monthly):</Box>
            <TextField
              onChange={(e) =>
                onChangeOrg("overrideEmailQuota", e.target.value)
              }
              size="small"
              type="number"
              placeholder={"1500"}
              value={organization?.overrideEmailQuota}
            />
          </Grid>
          <Grid item xs={12} container alignItems="center" gap={1}>
            <Box sx={styles.label}>Override Plan Name:</Box>
            <Select
              value={organization?.overridePlanName || "None"}
              disabled={loading}
              sx={{ minWidth: 200, ml: 2 }}
              onChange={(e) => onChangeOrg("overridePlanName", e.target.value)}
              size="small"
              displayEmpty
            >
              <MenuItem value="None">
                <em>None</em>
              </MenuItem>
              <MenuItem value={OverridePlanName.STARTER}>Starter</MenuItem>
              <MenuItem value={OverridePlanName.PRO}>Pro</MenuItem>
              <MenuItem value={OverridePlanName.VA}>Virtual Assistant</MenuItem>
              <MenuItem value={OverridePlanName.AUTOPILOT}>Autopilot</MenuItem>
            </Select>
          </Grid>

          <Typography variant="h6">Virtual Assistant Settings</Typography>

          <Grid item xs={12} container alignItems="center" gap={1}>
            <Box sx={styles.label}>Virtual Assistant Status:</Box>
            <Select
              value={organization?.vaStatus || ""}
              disabled={loading}
              sx={{ minWidth: 200, ml: 2 }}
              onChange={(e) => onChangeOrg("vaStatus", e.target.value)}
              size="small"
              displayEmpty
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              <MenuItem value={VAStatus.pending}>Pending</MenuItem>
              <MenuItem value={VAStatus.approved}>Approved</MenuItem>
              <MenuItem value={VAStatus.onboarding_paused}>
                Paused Onboarding
              </MenuItem>
            </Select>
          </Grid>
          {organization?.vaStatus === VAStatus.approved && (
            <Grid item xs={12} container alignItems="center" gap={1}>
              <Box sx={styles.label}>VA Management:</Box>
              <Link
                href={`/admin/va-management/${organization.id}`}
                target="_blank"
                sx={{ ml: 2 }}
              >
                Go To VA Management
              </Link>
            </Grid>
          )}
          <Grid item xs={12}>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6">Categories</Typography>

            {loading || topLevelCategories?.length === 0 ? (
              <CircularProgress />
            ) : (
              <Accordion sx={{ mb: 4, mt: 3 }}>
                <AccordionSummary>
                  Click on the card to see all users categories
                </AccordionSummary>
                <AccordionDetails>
                  <DetailedCategoryForm
                    topLevelCategories={topLevelCategories}
                    setNeedsUpdateCategory={setNeedsUpdateCategory}
                    selectedCategories={selectedCategories}
                    setSelectedCategories={setSelectedCategories}
                  />
                </AccordionDetails>
              </Accordion>
            )}
          </Grid>

          <LoadingButton
            variant="contained"
            onClick={handleSave}
            loading={saveLoading}
            disabled={loading}
          >
            Save
          </LoadingButton>
        </Grid>
      )}

      <ConfirmDialog
        header={`Delete User`}
        subheader={`Are you sure you want to delete this user?`}
        open={confirmDeleteDialogOpen}
        handleClose={() => setConfirmDeleteDialogOpen(false)}
        handleConfirm={handleConfirm}
        loading={deleteLoading}
      />
    </Box>
  );
}
